/**
 * FMS data utilities — flatten entity API responses.
 *
 * The entity API returns raw DB rows with a `data` JSONB column containing
 * extended fields (direction, mode, jobNo, customer, etc.). FMS pages need
 * these fields merged into the top-level row object for easy mapping.
 */

/** Flatten `data` JSONB into the row so `r.jobNo` works directly. */
export function flattenEntityRow(row: Record<string, unknown>): Record<string, unknown> {
  const data = row.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return { ...data as Record<string, unknown>, ...row };
  }
  return row;
}

/** Map entity API response items, flattening `data` JSONB. */
export function flattenEntityItems(res: unknown): Record<string, unknown>[] {
  const items = (res as { items?: unknown[] })?.items ?? (Array.isArray(res) ? res : []);
  return items.map((r) => flattenEntityRow(r as Record<string, unknown>));
}

/** Safe string accessor with fallback chain. */
export function str(row: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (v != null && v !== '') return String(v);
  }
  return '';
}

/** Format ISO datetime to short date string. */
export function fmtDate(val: unknown): string {
  if (!val) return '';
  const d = new Date(String(val));
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
}
